// covered-by: frontends/carrie/src/routes/ask-puntt/utils/__tests__/knownConnectorTools.test.ts
/**
 * Exact tool slug → connectorId map for Ask Puntt connector chips.
 *
 * Keep in sync with rembrandt connector specs + native tools:
 * - `services/rembrandt/temporal/activities/ask_puntt/connectors/*.py`
 *   (`fn_name` / `slug.lower()` from `build_connector_tools`)
 * - Native Asana tools in `tools.py` (`asana_integration`, `asana_typeahead`, …)
 * - Email actions (`generate_email_draft`, `send_email`)
 * - Teams tools in `connectors/microsoft_teams.py`
 *
 * Prefix heuristics in `matchToolToConnectorId` still cover Composio
 * `ASANA_*` / `WRIKE_*` / … slugs that are not listed here yet.
 */
export const EXACT_CONNECTOR_TOOL_NAMES: Readonly<Record<string, string>> = {
  // Native Asana (tools.py)
  asana_integration: "asana",
  asana_typeahead: "asana",
  asana_list_projects: "asana",
  asana_search: "asana",
  create_asana_task: "asana",
  // Asana custom Composio fn_names (no asana_ prefix)
  get_all_workspaces: "asana",
  get_task_comments: "asana",
  get_team_projects: "asana",
  get_workspace_projects: "asana",
  // Wrike
  wrike_search: "wrike",
  create_wrike_task: "wrike",
  get_wrike_custom_fields: "wrike",
  get_wrike_folders: "wrike",
  get_wrike_contacts: "wrike",
  get_wrike_task: "wrike",
  get_wrike_folder_tasks: "wrike",
  // Figma (custom fn_names; Composio slugs still match figma_*)
  download_figma_assets: "figma",
  get_figma_file_with_context: "figma",
  list_figma_projects: "figma",
  get_figma_component_with_usage: "figma",
  // Canva
  canva_ingest_design: "canva",
  canva_get_design_metadata: "canva",
  canva_list_user_designs: "canva",
  // Jira search wrapper
  jira_search: "jira",
  jira_get_issue_attachment_metadata: "jira",
  // Teams
  list_my_teams: "microsoft-teams",
  list_team_channels: "microsoft-teams",
  list_channel_messages: "microsoft-teams",
  get_message_replies: "microsoft-teams",
  post_channel_message: "microsoft-teams",
  // Email
  generate_email_draft: "email",
  send_email: "email",
};

/** Shared provider tools — attribute via args.provider, then displayName. */
export const PROVIDER_SHARED_TOOL_NAMES = [
  "read_provider_attachment",
  "provider_task_reviews",
] as const;

const PROVIDER_SHARED_TOOLS = new Set<string>(PROVIDER_SHARED_TOOL_NAMES);

function displayNameMentions(displayName: string, brand: string): boolean {
  return displayName.toLowerCase().includes(brand.toLowerCase());
}

function providerFromArgs(
  args: Record<string, unknown> | undefined,
): string | null {
  const raw = args?.provider;
  if (typeof raw !== "string") return null;
  const provider = raw.trim().toLowerCase();
  if (provider === "asana" || provider === "wrike") return provider;
  return null;
}

/**
 * Map a tool slug (+ optional display name / args) to a UI connectorId.
 * Returns null for built-ins and unrecognized tools.
 *
 * Shared by V1 chips and the V2 rail so both trees use the rembrandt map.
 */
export function matchToolToConnectorId(
  tool: string,
  displayName = "",
  args?: Record<string, unknown>,
): string | null {
  const name = tool.trim().toLowerCase();
  if (!name) return null;

  if (PROVIDER_SHARED_TOOLS.has(name)) {
    const fromArgs = providerFromArgs(args);
    if (fromArgs) return fromArgs;
    if (displayNameMentions(displayName, "Asana")) return "asana";
    if (displayNameMentions(displayName, "Wrike")) return "wrike";
    return null;
  }

  const exact = EXACT_CONNECTOR_TOOL_NAMES[name];
  if (exact) return exact;

  if (name.startsWith("asana_")) return "asana";

  if (name.startsWith("wrike_") || name.startsWith("get_wrike_")) {
    return "wrike";
  }

  if (
    name.startsWith("figma_") ||
    name.startsWith("get_figma_") ||
    name.startsWith("list_figma_")
  ) {
    return "figma";
  }

  if (name.startsWith("canva_")) return "canva";

  if (name.startsWith("googledrive_") || name.startsWith("google_drive_")) {
    return "google_drive";
  }

  if (name.startsWith("share_point_") || name.startsWith("sharepoint_")) {
    return "sharepoint";
  }

  if (name.startsWith("jira_")) return "jira";

  if (
    name.startsWith("microsoft_teams_") ||
    name.startsWith("microsoft-teams_") ||
    name.startsWith("teams_")
  ) {
    return "microsoft-teams";
  }

  return null;
}
